import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Snackbar, Alert, CircularProgress, Tooltip
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import {
  getAllReviewFrequencies,
  createReviewFrequency,
  updateReviewFrequency,
  deleteReviewFrequency
} from '../../services/reviewFrequencyService';
import { ReviewFrequency } from '../../services/api';

const ReviewFrequencySetup: React.FC = () => {
  const navigate = useNavigate();
  const [frequencies, setFrequencies] = useState<ReviewFrequency[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<string>('');

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'add'>('edit');
  const [currentFrequency, setCurrentFrequency] = useState<ReviewFrequency>({
    id: 0,
    name: '',
    display_label: '',
    description: ''
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    fetchFrequencies();
  }, []);

  const fetchFrequencies = async () => {
    setLoading(true);
    try {
      const data = await getAllReviewFrequencies();
      setFrequencies(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch review frequencies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (name: string) => {
    setDeleteTarget(name);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget('');
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteReviewFrequency(deleteTarget);
      setSnackbar({
        open: true,
        message: `Review frequency "${deleteTarget}" deleted successfully`,
        severity: 'success'
      });
      fetchFrequencies();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to delete review frequency: ${(err as Error).message}`,
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenEditDialog = (frequency: ReviewFrequency) => {
    setEditMode('edit');
    setCurrentFrequency(frequency);
    setOpenEditDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditMode('add');
    setCurrentFrequency({
      id: 0,
      name: '',
      display_label: '',
      description: ''
    });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setCurrentFrequency(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitFrequency = async () => {
    try {
      if (editMode === 'add') {
        await createReviewFrequency({
          name: currentFrequency.name,
          display_label: currentFrequency.display_label,
          description: currentFrequency.description
        });
        setSnackbar({
          open: true,
          message: `Review frequency "${currentFrequency.name}" created successfully`,
          severity: 'success'
        });
      } else {
        await updateReviewFrequency({
          name: currentFrequency.name,
          display_label: currentFrequency.display_label,
          description: currentFrequency.description
        });
        setSnackbar({
          open: true,
          message: `Review frequency "${currentFrequency.name}" updated successfully`,
          severity: 'success'
        });
      }
      fetchFrequencies();
      handleCloseEditDialog();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to ${editMode === 'add' ? 'create' : 'update'} review frequency: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const isFormValid = () => {
    return (
      currentFrequency.name.trim() !== '' &&
      currentFrequency.display_label.trim() !== '' &&
      currentFrequency.description.trim() !== ''
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TitleBar title="" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

        {/* Header Row with Arrow Icon and Title + Add Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/organization-setup')}
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1,
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              Review Frequency Setup
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Review Frequency
          </Button>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : frequencies.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
              No review frequencies found. Click the button above to add one.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ tableLayout: 'fixed', fontSize: '0.85rem' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Display Label</TableCell>
                    <TableCell width="40%" sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell width="20%" align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {frequencies.map((frequency) => (
                    <TableRow key={frequency.name}>
                      <TableCell>{frequency.name}</TableCell>
                      <TableCell>{frequency.display_label}</TableCell>
                      <TableCell title={frequency.description || ''}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {frequency.description ? frequency.description.split(' ').slice(0, 6).join(' ') : ''}
                          {frequency.description && frequency.description.split(' ').length > 6 && (
                            <>
                              ...<Tooltip title={frequency.description}><InfoIcon fontSize="small" sx={{ ml: 0.5 }} /></Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(frequency)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(frequency.name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the review frequency "{deleteTarget}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth maxWidth="md">
        <DialogTitle>{editMode === 'edit' ? 'Edit' : 'Add'} Review Frequency</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              name="name"
              label="Name"
              value={currentFrequency.name}
              onChange={handleInputChange}
              fullWidth
              required
              disabled={editMode === 'edit'}
              helperText={editMode === 'edit' ? 'Name cannot be modified' : 'Unique identifier'}
            />
            <TextField
              name="display_label"
              label="Display Label"
              value={currentFrequency.display_label}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              name="description"
              label="Description"
              value={currentFrequency.description}
              onChange={handleInputChange}
              fullWidth
              required
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
  <Button onClick={handleCloseEditDialog}>Cancel</Button>
  <Button
    variant="contained"
    color="primary"
    onClick={handleSubmitFrequency}
    disabled={!isFormValid()}
    sx={{ textTransform: 'none' }}
  >
    {editMode === 'add' ? 'Create' : 'Update'}
  </Button>
</DialogActions>

      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewFrequencySetup;
